import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Card,
  Button,
  Icon,
  Divider,
  InputSelect,
  Form,
  InputText,
  InputDatePicker
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import { HomeDto } from '@lmc/ts-common'
import { ConsentStatus } from '@lmc/common/types'
import { getNominative } from 'common/utils'

const STATUS_INFO_TEXT_KEYS: Record<ConsentStatus, string> = {
  [ConsentStatus.noStatus]: 'infoText.noStatus',
  [ConsentStatus.consentNotObtained]: 'infoText.consentNotObtained',
  [ConsentStatus.completed]: 'infoText.completed',
  [ConsentStatus.planned]: 'infoText.planned',
  [ConsentStatus.requested]: 'infoText.requested',
  [ConsentStatus.scheduled]: 'infoText.scheduled',
  [ConsentStatus.postponed]: 'infoText.postponed',
  [ConsentStatus.cancelled]: 'infoText.cancelled',
  [ConsentStatus.withdrawn]: 'infoText.withdrawn',
  [ConsentStatus.refused]: 'infoText.refused',
  [ConsentStatus.provided]: 'infoText.provided'
}

const SIGN_FORM_STATUSES: ConsentStatus[] = [
  ConsentStatus.cancelled,
  ConsentStatus.withdrawn,
  ConsentStatus.refused,
  ConsentStatus.provided
]

type StatusOptionConfig = {
  labelKey?: string
  options: {
    labelKey: string
    value: ConsentStatus
  }[]
}

const STATUS_OPTION_GROUPS: StatusOptionConfig[] = [
  {
    options: [
      {
        labelKey: 'options.noStatus',
        value: ConsentStatus.noStatus
      }
    ]
  },
  {
    labelKey: 'options.saveForm',
    options: [
      {
        labelKey: 'options.consentNotObtained',
        value: ConsentStatus.consentNotObtained
      },
      {
        labelKey: 'options.completed',
        value: ConsentStatus.completed
      },
      {
        labelKey: 'options.planned',
        value: ConsentStatus.planned
      },
      {
        labelKey: 'options.requested',
        value: ConsentStatus.requested
      },
      {
        labelKey: 'options.scheduled',
        value: ConsentStatus.scheduled
      },
      {
        labelKey: 'options.postponed',
        value: ConsentStatus.postponed
      }
    ]
  },
  {
    labelKey: 'options.signForm',
    options: [
      {
        labelKey: 'options.cancelled',
        value: ConsentStatus.cancelled
      },
      {
        labelKey: 'options.withdrawn',
        value: ConsentStatus.withdrawn
      },
      {
        labelKey: 'options.refused',
        value: ConsentStatus.refused
      },
      {
        labelKey: 'options.provided',
        value: ConsentStatus.provided
      }
    ]
  }
]

export type GroupedSelectOption = {
  label: string
  value: string
}

export type GroupedSelectOptions = {
  label: string
  options: GroupedSelectOption[]
}[]

type ConsentConfirmationCardProps = {
  onConfirm?: () => void
  onSign?: () => void
  onStatusChange?: (value: ConsentStatus) => void
  selectedStatus?: ConsentStatus
  testId?: string
}

const ConsentConfirmationCard: React.FC<ConsentConfirmationCardProps> = ({
  onConfirm,
  onSign,
  onStatusChange,
  selectedStatus,
  testId
}) => {
  const { t } = useTranslation()

  const translate = (key: string, variable?: any) =>
    t(
      `consent.createConsentForms.components.consentConfirmationCard.${key}`,
      variable
    )

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: HomeDto } }) =>
      state.global.home.preferredPeopleNominative
  )

  const preferredNominative = getNominative(
    preferredPeopleNominative,
    'resident'
  ).toLocaleLowerCase()

  const [internalStatus, setInternalStatus] = useState<ConsentStatus>(
    ConsentStatus.noStatus
  )
  const currentStatus = selectedStatus ?? internalStatus
  const isSignStatus =
    !!currentStatus && SIGN_FORM_STATUSES.includes(currentStatus)

  const handleStatusChange = (value: string) => {
    const statusValue = value as ConsentStatus
    if (!selectedStatus) {
      setInternalStatus(statusValue)
    }
    if (onStatusChange) {
      onStatusChange(statusValue)
    }
  }

  const isConfirmDisabled =
    currentStatus === ConsentStatus.noStatus || !currentStatus

  const getInfoText = (): string => {
    const safeStatus = currentStatus ?? ConsentStatus.noStatus
    const infoKey = STATUS_INFO_TEXT_KEYS[safeStatus]
    return translate(infoKey)
  }

  const baseOptions =
    STATUS_OPTION_GROUPS.find((group) => !group.labelKey)?.options ?? []

  const groupedOptionConfigs = STATUS_OPTION_GROUPS.filter(
    (group) => !!group.labelKey
  )

  const options = baseOptions.map((option) => ({
    label: translate(option.labelKey),
    value: option.value
  }))

  const groupedOptions: GroupedSelectOptions = groupedOptionConfigs.map(
    (group) => ({
      label: group.labelKey ? translate(group.labelKey) : '',
      options: group.options.map((option) => ({
        label: translate(option.labelKey),
        value: option.value
      }))
    })
  )

  const handleAction = () => {
    if (isSignStatus) {
      onSign?.()
      return
    }

    onConfirm?.()
  }

  return (
    <StyledCard
      data-testid={
        testId
          ? `${testId}-consent-confirmation-card`
          : 'consent-confirmation-card'
      }
    >
      <TopSection>
        <QuestionText level={1}>
          {translate('question', { nominative: preferredNominative })}
        </QuestionText>
        <Button
          variant="primary"
          onClick={handleAction}
          disabled={isConfirmDisabled}
          data-testid={
            testId
              ? `${testId}-confirm-button`
              : 'consent-confirmation-confirm-button'
          }
        >
          <Icon name="CheckOutlined" size={16} />
          <Space>
            {isSignStatus
              ? translate('signButton')
              : translate('confirmButton')}
          </Space>
        </Button>
      </TopSection>

      <StyledDivider />

      <StatusSection>
        <LabelContainer>
          <LabelText level={1}>{translate('consentStatusLabel')}</LabelText>
          <Asterisk>*</Asterisk>
        </LabelContainer>

        <HelperText level={2}>{translate('helperText')}</HelperText>

        <SelectContainer>
          <Form>
            <StyledInputSelect
              testId={
                testId
                  ? `${testId}-consent-status-select`
                  : 'consent-status-select'
              }
              options={options}
              value={currentStatus}
              onChange={handleStatusChange}
              groupedOptions={groupedOptions}
              layout="column"
              defaultValue={ConsentStatus.noStatus}
              type="consentStatus"
              formItemstyle={selectFormItemStyle}
            />
          </Form>
        </SelectContainer>
        <InfoText>{getInfoText()}</InfoText>

        {currentStatus === ConsentStatus.postponed && (
          <ExtraSection>
            <LabelContainer>
              <LabelText level={1}>
                {translate('extra.postponed.label')}
              </LabelText>
            </LabelContainer>
            <HelperText level={2}>
              {translate('extra.postponed.helper')}
            </HelperText>
            <StyledInputDatePicker
              type="lastReviewDate"
              wrapFormItem={false}
              localization={{
                placeholder: translate('extra.postponed.placeholder'),
                errorMessage: ''
              }}
            />
          </ExtraSection>
        )}

        {currentStatus === ConsentStatus.withdrawn && (
          <ExtraSection>
            <LabelContainer>
              <LabelText level={1}>
                {translate('extra.withdrawn.label')}
              </LabelText>
              <Asterisk>*</Asterisk>
            </LabelContainer>
            <HelperText level={2}>
              {translate('extra.withdrawn.helper')}
            </HelperText>
            <StyledInputText
              type="withdrawalReason"
              layout="column"
              placeholder={translate('extra.withdrawn.placeholder')}
            />
          </ExtraSection>
        )}
      </StatusSection>
    </StyledCard>
  )
}

const StyledCard = styled(Card)`
  padding: 24px;
`

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const Space = styled.span`
  margin-left: 8px;
`

const StatusSection = styled.div`
  display: flex;
  flex-direction: column;
`

const LabelContainer = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 8px;
`

const Asterisk = styled.span`
  color: red;
  margin-left: 2px;
`

const SelectContainer = styled.div``

const ExtraSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`

const StyledInputText = styled(InputText)`
  width: 390px;
  height: 36px;
`

const StyledInputDatePicker = styled(InputDatePicker)`
  width: 390px;
  .ant-picker {
    width: 100%;
  }
`

const QuestionText = styled(Text)`
  font-size: 24px;
  font-weight: 500;
`

const StyledDivider = styled(Divider)`
  margin: 16px 0;
`

const LabelText = styled(Text)`
  font-size: 16px;
  font-weight: 500;
`

const HelperText = styled(Text)`
  font-size: 12px;
  margin-bottom: 16px;
`

const StyledInputSelect = styled(InputSelect)`
  width: 390px;
  height: 36px;
`

const InfoText = styled(Text)`
  font-size: 12px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const selectFormItemStyle = {
  marginBottom: 0
}

export default ConsentConfirmationCard
